<script setup lang="ts">
import type {ShopProduct} from "~~/types/shop";
import { usePriceSplit } from '~/composables/usePriceSplit'
import {useCartStore} from "~~/stores/cart";
import LGMapModal from "~/components/partials/LGMapModal.vue";

const { t } = useI18n()
const localePath = useLocalePath()
const cart = useCartStore()
const props = defineProps<{
    product: ShopProduct
}>()

const backgroundStyles = computed(() => {
    const imgUrl = '/images/partials/shop-item-discount.svg'
    return { backgroundImage: `url('${imgUrl}')` }
})

const price = props.product.price_cents
const originalPrice = props.product.original_price_cents

onMounted(() => {
    if (!cart.cart) cart.fetch()
})
const inCart = computed(() =>
    !!cart.cart?.items?.some(i => i.product_id === props.product.product_id)
)

const addToCart = async (productId: number) => {
    if (inCart.value) return
    await cart.add(productId)
    navigateTo(localePath('kosarica'));
}

const isMapOpen = ref(false)
const openMap = () => { isMapOpen.value = true }

const mapPins = computed(() =>
    props.product.guide_points?.map(p => ({
        lat: p.lat,
        lng: p.lng,
    })) ?? []
)

</script>

<template>
    <div class="grid grid-cols-3 gap-4 md:gap-16 items-center w-full bg-white rounded-[65px] shadow-[-4px_4px_0px_0px_#6969DBB2] md:shadow-[-8px_8px_0px_0px_#6969DBB2] py-10 px-4 md:py-8 md:px-16 relative">
        <div v-if="product.original_price_cents > product.price_cents" class="h-[52px] w-[85px] md:h-[94px] md:w-[159px] absolute top-10 -left-[7px] md:-left-[12px] bg-contain bg-no-repeat bg-top flex justify-center items-center" :style="backgroundStyles">
            <span class="discount text-white text-[26px] leading-[30px] md:text-[47px] md:leading-[54px] font-bold -mt-[12px]">-{{ (100 - product.price_cents / product.original_price_cents * 100).toFixed(0) }}%</span>
        </div>
        <div class="col-span-1">
            <NuxtImg :src="`/images/product/shop-product-image-${product.product_image_type}.svg`" alt="Title" class="w-full h-auto" />
        </div>
        <div class="col-span-2 flex flex-col gap-2 md:gap-2">
            <h3 class="text-secondary text-[20px] leading-[24px] md:text-[28px] md:leading-[38px] uppercase">{{ product.title }}</h3>
            <p class="text-[#222222] text-[13px] leading-[16px] md:text-[18px] md:leading-[26px]">{{ product.description }}</p>
            <div class="flex flex-row justify-between items-center gap-2">
                <div class="flex flex-col sm:flex-row gap-2">
                    <div class="flex flex-row justify-start items-center gap-[5px] md:gap-[15px] whitespace-nowrap flex-1">
                        <NuxtImg src="/images/partials/shop-item-clock.svg" alt="Time" class="h-[15px] md:h-[26px] w-auto block" />
                        <span class="text-[#030340] font-bold">{{ product.license_duration_days }} {{ t("partials.shopItem.days") }}</span>
                    </div>
                    <div class="flex flex-row justify-start items-center gap-[5px] md:gap-[15px] whitespace-nowrap flex-1">
                        <NuxtImg src="/images/partials/shop-item-person.svg" alt="Age" class="h-[15px] md:h-[26px] w-auto block" />
                        <span v-if="product.min_years != product.max_years" class="text-[#030340] font-bold">{{ product.min_years }} - {{ product.max_years }} {{ t("partials.shopItem.years") }}</span>
                        <span v-else class="text-[#030340] font-bold">{{ product.min_years }} {{ t("partials.shopItem.years") }}</span>
                    </div>
                </div>
                <div class="flex flex-col-reverse lg:flex-row justify-end items-end lg:items-center gap-2">
                    <span v-if="originalPrice > price" class="price text-[#030340] text-[15px] leading-[15px] md:text-[22px] md:leading-[26px] flex flex-row items-start relative after:absolute after:h-[1px] after:top-[50%] after:bg-[#030340] after:w-full"><span>{{ usePriceSplit(originalPrice).euros }}</span><span class="text-[12px] leading-[12px] md:text-[12px] md:leading-[12px]">{{ usePriceSplit(originalPrice).cents }}</span> {{ product.currency_symbol }}</span>
                    <span class="price text-[#F46C62] font-bold text-[26px] leading-[26px] md:text-[47px] md:leading-[45px] flex flex-row items-start"><span>{{ usePriceSplit(price).euros }}</span><span class="text-[13px] leading-[13px] md:text-[24px] md:leading-[24px]">{{ usePriceSplit(price).cents }}</span> {{ product.currency_symbol }}</span>
                </div>
            </div>
            <div class="flex flex-row gap-2 md:gap-4.5">
                <UButton v-if="!inCart" color="primary" variant="solid" :disabled="cart.loading" @click="addToCart(product.product_id)">{{ t("partials.shopItem.addToCart") }}</UButton>
                <UButton v-else color="primary" variant="solid" :to="localePath('kosarica')">{{ t("partials.shopItem.inCart") }}</UButton>
                <UButton
                    color="primary"
                    variant="outline"
                    class="hidden md:inline-block"
                    @click="openMap"
                >
                    <span>{{ t('partials.shopItem.map') }}</span>
                </UButton>
                <UButton
                    color="primary"
                    variant="outline"
                    class="md:!hidden hover:bg-transparent focus:bg-transparent active:bg-transparent"
                    aria-label="Open map"
                    @click="openMap"
                >
                    <UIcon name="i-lyra-guides-map" />
                </UButton>
                <LGMapModal v-model:open="isMapOpen" :pins="mapPins" />
            </div>
        </div>
    </div>
</template>

<style scoped>
.price, .discount {
    font-family: "Quicksand", sans-serif;
}
</style>
